// DEMO_MODE and BUILD_SENTRY_DSN are injected at build time by esbuild.
declare const DEMO_MODE: boolean;
declare const BUILD_SENTRY_DSN: string;

import { App, AppOptions, WorkbenchFactory } from "./app.js";
import { AiInlineCompletionService } from "./lib/completion/inline-completion-service.js";
import { CodeMirrorEditor } from "./components/codemirror-editor.js";
import { WritingAssistant } from "./agents/writing-assistant.js";
import { ChatSession } from "./lib/chat/chat-session.js";
import { DocumentManager } from "./lib/document/document-manager.js";
import { DocumentPath } from "./lib/document/document-service.js";
import { WebDavDocumentStore } from "./lib/document/stores/webdav-document-store.js";
import { FileSystemDocumentStore } from "./lib/document/stores/file-system-document-store.js";
import { BrowserChatHistory } from "./lib/history/browser-chat-history.js";
import { ConsoleLogger } from "./lib/logging/console-logger.js";
import { LoggerFactory } from "./lib/logging/logger-factory.js";
import { PlatformRegistry } from "./lib/platform/platform-registry.js";
import { ToolRegistry } from "./lib/tools/tools-registry.js";
import { ComponentFactory } from "./lib/ui/component-factory.js";
import { IUserInteraction } from "./lib/ui/user-interaction.js";
import { Workbench } from "./lib/workbench.js";
import { OllamaPlatform } from "./platform/ollama/ollama-platform.js";
import { OllamaStreamReader } from "./platform/ollama/ollama-stream-reader.js";
import { AnthropicPlatform } from "./platform/anthropic/anthropic-platform.js";
import { AnthropicStreamReader } from "./platform/anthropic/anthropic-stream-reader.js";
import { OpenAIPlatform } from "./platform/openai/openai-platform.js";
import { OpenAIStreamReader } from "./platform/openai/openai-stream-reader.js";
import { GeminiPlatform } from "./platform/gemini/gemini-platform.js";
import { GeminiStreamReader } from "./platform/gemini/gemini-stream-reader.js";
import { MistralPlatform } from "./platform/mistral/mistral-platform.js";
import { MistralStreamReader } from "./platform/mistral/mistral-stream-reader.js";
import { UrlResolver } from "./lib/url-resolver.js";
import { Configuration, ConfigurationManager } from "./lib/configuration/configuration-service.js";
import { initSentry } from "./lib/monitoring/sentry.js";

const WELCOME_DOCUMENT_PATH = DocumentPath.parse("/getting-started.md");

const WELCOME_CONTENT = `# Welcome to Proseaic

ProseAiC is a markdown editor with AI writing assistance. This demo runs entirely in your browser. Documents and API Keys are only stored in your browser's private storage.

## Setting Up AI

Open **Settings** (gear icon, top right) and add your API key for one or more providers:

| Provider | Notes |
|---|---|
| Anthropic | Claude models — recommended for writing |
| OpenAI | GPT models |
| Google Gemini | Gemini models |
| Mistral | Mistral models |
| Ollama | Local models — must be running at \`http://localhost:11434\` |

Once a key is entered, the AI chat panel becomes available.

## Using the Editor

- Write markdown directly — headings, lists, bold, italic, and code blocks all render with syntax highlighting
- **AI chat** (speech bubble icon): ask the AI to edit, rewrite, summarise, or generate content in your document
- **AI autocomplete**: inline suggestions appear as you type; press Tab to accept

## Document Storage

In this demo, documents are saved to your browser's [Origin Private File System](https://developer.mozilla.org/en-US/docs/Web/API/File_System_API/Origin_private_file_system).
They persist across sessions but are private to this browser and origin.

---

For the full self-hosted version with server-side storage, see the project README.
`;

async function seedWelcomeDocument(documentManager: DocumentManager): Promise<void> {
  const docs = await documentManager.listDocuments();
  if (docs.length === 0) {
    await documentManager.createDocument(WELCOME_DOCUMENT_PATH, WELCOME_CONTENT);
  }
}

// This is the composition root of the application, where the dependencies are configured.
(async function initialize(): Promise<void> {

  // Injecting document and customElementsRegistry from globalThis allows us to mock them in tests and also makes it explicit that our component factory depends on these browser APIs for managing UI components.
  const { document: _document, customElements: _customElementsRegistry } = globalThis;

  // The logger factory is responsible for creating loggers for different components of the app. In this case, it creates ConsoleLoggers that log to the browser console, but in a more complex app it could create loggers that send logs to a server or integrate with a logging framework.
  const loggerFactory: LoggerFactory = (componentName: string) => new ConsoleLogger(componentName);
  const logger = loggerFactory("App");

  // The component factory is responsible for creating instances of UI components and injecting dependencies into them.
  // It also handles defining custom elements in the DOM as needed.
  const componentFactory = new ComponentFactory(loggerFactory, _document, _customElementsRegistry, "ui");

  // The configuration manager is responsible for managing user-configurable settings in the app.
  // It persists settings to local storage and provides an interface for getting and setting configuration values, as well as listening for changes to configuration.
  const configuration = new ConfigurationManager(localStorage);

  initSentry(
    DEMO_MODE ? BUILD_SENTRY_DSN : configuration.get("monitoring.sentry.dsn"),
    DEMO_MODE ? !!BUILD_SENTRY_DSN : configuration.get("monitoring.sentry.enabled") === "true"
  );

  // Injecting fetch from globalThis allows us to easily mock it in tests, and also makes it explicit that our platform implementations depend on fetch for making API calls.
  const fetchFunction = globalThis.fetch.bind(globalThis);

  const platformRegistry = new PlatformRegistry(loggerFactory);
  
  // API keys are read lazily so changes in Settings take effect immediately without a page reload.
  // Endpoints are read once at startup; changing an endpoint requires a page reload.
  const getApiKey = (key: keyof Configuration) => () => configuration.get(key) ?? "";
  const getEndpoint = (key: keyof Configuration, defaultUrl: string) => configuration.get(key) || defaultUrl;
  
  // In demo mode Ollama is accessed directly; in server mode it is proxied through the Express server.
  const ollamaDefaultEndpoint = DEMO_MODE ? "http://localhost:11434" : "/ollama";
  platformRegistry.registerMany([
    new OllamaPlatform(loggerFactory, fetchFunction, getApiKey("ai.platform.ollama.api_key"),    () => new OllamaStreamReader(),    new UrlResolver(getEndpoint("ai.platform.ollama.endpoint", ollamaDefaultEndpoint), document.baseURI)),
    new AnthropicPlatform(loggerFactory, fetchFunction, getApiKey("ai.platform.anthropic.api_key"), () => new AnthropicStreamReader(), new UrlResolver(getEndpoint("ai.platform.anthropic.endpoint", "https://api.anthropic.com"), document.baseURI)),
    new OpenAIPlatform(loggerFactory, fetchFunction, getApiKey("ai.platform.openai.api_key"),    () => new OpenAIStreamReader(),    new UrlResolver(getEndpoint("ai.platform.openai.endpoint",    "https://api.openai.com"), document.baseURI)),
    new GeminiPlatform(loggerFactory, fetchFunction, getApiKey("ai.platform.gemini.api_key"),    () => new GeminiStreamReader(),    new UrlResolver(getEndpoint("ai.platform.gemini.endpoint",    "https://generativelanguage.googleapis.com"), document.baseURI)),
    new MistralPlatform(loggerFactory, fetchFunction, getApiKey("ai.platform.mistral.api_key"),  () => new MistralStreamReader(),   new UrlResolver(getEndpoint("ai.platform.mistral.endpoint",   "https://api.mistral.ai"), document.baseURI)),
  ]);

  const toolRegistry = new ToolRegistry();

  // Chat history is saved to local storage under the "chat_history" key.
  const history = new BrowserChatHistory("chat_history", localStorage);

  // Create the chat session, which is the main interface for the UI to interact with the underlying platform and tools.
  // The chat session is responsible for managing the state of the current chat, submitting user prompts to the platform, and invoking tools as needed.
  // It is injected with the platform registry, tool registry, and model registry so that it can perform these functions.
  const chatSession = new ChatSession(
    loggerFactory,
    platformRegistry,
    history,
    toolRegistry,
    new WritingAssistant()
  );

  // The document manager is responsible for managing documents in the app.
  // It provides an interface for creating, reading, updating, renaming, and deleting documents, as well as tracking which documents are dirty (i.e. have unsaved changes).
  const documentManager = new DocumentManager([], localStorage);
  if (DEMO_MODE) {
    documentManager.registerMany([
      new FileSystemDocumentStore(() => navigator.storage.getDirectory()),
    ]);
    await seedWelcomeDocument(documentManager);
  } else {
    documentManager.registerMany([
      new WebDavDocumentStore(window.location.origin),
    ]);
  }

  // The inline completion service provides AI-powered completions for the editor.
  // It is injected with the configuration and platform registry so that it can determine which model to use and how to call the platform's API to get completions.
  const completionService = new AiInlineCompletionService(
    configuration,
    platformRegistry
  );

  const editorFactory = async (format: string) => {
    switch (format) {
      case "text/markdown":
      case "markdown":
        return componentFactory.create(CodeMirrorEditor, {
          completionService
        });
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  };

  // The workbench factory is responsible for creating the main workbench component of the app, which manages open documents and editors.
  const workbenchFactory: WorkbenchFactory = (ui: IUserInteraction) => {
    return new Workbench(
      ui,
      componentFactory,
      documentManager,
      documentManager,
      completionService,
      editorFactory
    )
  }


  const options: AppOptions = {
    global: globalThis,
    componentFactory,
    completionService,
    configurationService: configuration,
    logger,
    chatSession,
    platformService: platformRegistry,
    toolService: toolRegistry,
    documentService: documentManager,
    workbenchFactory,
  };

  await App.create(options);
})();
